/**
 * session — captain session storage for the proxied-auth flow.
 *
 * The browser no longer uses the Supabase JS client for auth. Login and
 * refresh go through the Express server (/api/auth/*), which hands back the
 * Supabase session tokens. We persist them here so a reload keeps the captain
 * signed in, and so the axios layer (lib/api.js) can attach the access token
 * and refresh it on expiry.
 *
 * Shape stored under STORAGE_KEY:
 *   { access_token, refresh_token, expires_at, user: { id, email } }
 *
 * localStorage is used (same as the previous Supabase default) — acceptable
 * for an internal 2-captain dashboard. All access is wrapped in try/catch so
 * SSR/prerender (react-snap, where localStorage is absent) never throws.
 */

const STORAGE_KEY = 'nc_captain_session';

export function getSession() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function setSession(session) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
  } catch {
    /* storage unavailable (prerender / private mode) — ignore */
  }
}

export function clearSession() {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

export function getAccessToken() {
  return getSession()?.access_token || null;
}

export function getRefreshToken() {
  return getSession()?.refresh_token || null;
}

// Event the axios layer dispatches when a refresh fails, so AuthContext can
// drop the session and bounce the user to the login page.
export const AUTH_EXPIRED_EVENT = 'nc:auth-expired';
